/**
 * US 12.2 — a meeting's "Action items". Each item is a task in the Squad
 * chosen for the meeting (one Squad for a whole recurring series); the item
 * reads its title, owner, due date and status from that task, so a change in
 * either place shows in the other within seconds. Removing an item asks
 * whether to delete or keep its task. Attendees outside the Squad can read
 * the items but not change them.
 *
 * Used in the calendar event panel and on the call screen.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ListChecks, Plus, Sparkles, X } from "lucide-react";
import { cn } from "@weco/ui";
import { useHubs } from "../hubs/api";
import { Modal } from "../hubs/parts";
import { taskApi, myTimeZone, type TaskApiError } from "../task/api";
import { DateField } from "../task/DatePicker";
import { bulkApi } from "../views/api";
import { FindTasksDialog } from "../mochi/FindTasksDialog";
import { MochiError, mochiApi } from "../mochi/api";
import { captureApi, type MeetingItem, type MeetingItems } from "./api";

/** US 13.6 — the first of these the meeting twin transcribed. */
async function findInMeeting(refs: string[], notTranscribed: string) {
  for (const ref of refs.filter(Boolean)) {
    try {
      return await mochiApi.meeting({ ref });
    } catch (err) {
      if (!(err instanceof MochiError) || err.status !== 404) throw err;
    }
  }
  return { card: null, message: notTranscribed, items: [] as [] };
}

const key = (eventKey: string) => ["tasks", "meeting", eventKey] as const;

export function MeetingActionItems({ eventKey, eventTitle, compact, meetingRefs = [] }: { eventKey: string; eventTitle: string; compact?: boolean; /** The room(s) the meeting ran in, for Find tasks. */ meetingRefs?: string[] }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  // Changes made in Tasks reach the notes within 5 seconds (US 12.2).
  const q = useQuery({ queryKey: key(eventKey), queryFn: () => captureApi.meetingItems(eventKey), refetchInterval: 4000, enabled: !!eventKey });
  const data = q.data;
  const hubs = useHubs().data ?? [];
  const squads = useMemo(() => hubs.flatMap((h) => h.squads.filter((s) => h.can_admin || s.my_role !== null).map((s) => ({ hub: h, squad: s }))), [hubs]);
  const [text, setText] = useState("");
  const [owner, setOwner] = useState("");
  const [due, setDue] = useState<{ date: string | null; time: string | null }>({ date: null, time: null });
  const [removing, setRemoving] = useState<MeetingItem | null>(null);
  const [finding, setFinding] = useState(false);
  const set = (next: MeetingItems) => qc.setQueryData(key(eventKey), next);
  const refetch = () => void qc.invalidateQueries({ queryKey: key(eventKey) });
  const fail = (err: unknown) => toast.error((err as TaskApiError)?.message || t("tasks.hubs.actionFailed", { defaultValue: "Couldn't save your change. Try again." }));

  if (!data) return q.isLoading ? <div className="h-16 rounded-2xl bg-muted/40 animate-pulse" aria-busy="true" /> : null;
  const editable = data.can_edit;
  const members = data.squad?.members ?? [];

  const add = () => {
    if (!text.trim()) return;
    captureApi.addMeetingItem(eventKey, { text: text.trim(), owner: owner || undefined, due_date: due.date }).then((next) => {
      set(next);
      setText("");
      setOwner("");
      setDue({ date: null, time: null });
    }, fail);
  };
  const patch = (item: MeetingItem, body: Parameters<typeof taskApi.update>[1]) => {
    if (!item.task.id) return;
    taskApi.update(item.task.id, body).then(refetch, fail);
  };

  return (
    <section aria-label={t("tasks.meeting.title", { defaultValue: "Action items" })} className={cn("rounded-2xl border border-border", compact ? "p-3" : "p-4")}>
      <header className="flex items-center gap-2 mb-3">
        <ListChecks className="size-4 text-muted-foreground" />
        <h3 className="text-sm font-bold flex-1">{t("tasks.meeting.title", { defaultValue: "Action items" })}</h3>
        {data.squad && <span className="text-xs text-muted-foreground truncate">{data.squad.hub_name} / {data.squad.name}</span>}
        <button
          type="button"
          onClick={() => setFinding(true)}
          className="h-7 px-2.5 rounded-lg border border-border text-xs font-semibold flex items-center gap-1.5 hover:bg-muted shrink-0"
        >
          <Sparkles className="size-3.5 text-primary" /> {t("tasks.findTasks.button", { defaultValue: "Find tasks" })}
        </button>
      </header>
      {finding && (
        <FindTasksDialog
          load={() => findInMeeting([eventKey, ...meetingRefs], t("tasks.findTasks.notTranscribed", { defaultValue: "Mochi didn't transcribe this meeting, so there's nothing to read." }))}
          onClose={() => setFinding(false)}
        />
      )}
      {!data.squad ? (
        squads.length ? (
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>{t("tasks.meeting.pickSquad", { defaultValue: "Squad for this meeting's tasks" })}</span>
            <select
              defaultValue=""
              onChange={(e) => e.target.value && captureApi.setMeetingSquad(eventKey, e.target.value, eventTitle).then(set, fail)}
              className="h-9 px-2.5 rounded-xl border border-border bg-background text-foreground text-xs"
            >
              <option value="">{t("tasks.hubs.newTask.pickSquad", { defaultValue: "Choose a squad" })}</option>
              {squads.map(({ hub, squad }) => (
                <option key={squad.id} value={squad.id}>
                  {hub.name} / {squad.name}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <p className="text-xs text-muted-foreground">{t("tasks.meeting.noSquads", { defaultValue: "Join a squad to turn action items into tasks." })}</p>
        )
      ) : (
        <>
          {data.items.length === 0 && <p className="text-xs text-muted-foreground mb-2">{t("tasks.meeting.empty", { defaultValue: "No action items yet." })}</p>}
          <ul className="space-y-1.5">
            {data.items.map((item) => (
              <li key={item.id} className={cn("group flex items-center gap-2 text-sm", compact && "flex-wrap pb-1.5 border-b border-border/60 last:border-0")}>
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={item.task.done}
                  disabled={!editable || !item.task.id}
                  aria-label={item.task.done ? t("tasks.task.markOpen", { defaultValue: "Mark as not done" }) : t("tasks.task.markDone", { defaultValue: "Mark as done" })}
                  onClick={() => item.task.id && bulkApi.run({ action: "update", ids: [item.task.id], patch: { done: !item.task.done } }).then(refetch, fail)}
                  className={cn("w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center", item.task.done ? "bg-green-500 border-green-500 text-white" : "border-border")}
                >
                  {item.task.done && <Check className="size-2.5" strokeWidth={3} />}
                </button>
                {editable ? (
                  <input
                    defaultValue={item.text}
                    key={item.text}
                    onBlur={(e) => e.target.value.trim() && e.target.value.trim() !== item.text && patch(item, { title: e.target.value.trim() })}
                    onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
                    aria-label={t("tasks.meeting.itemText", { defaultValue: "Action item" })}
                    className={cn("flex-1 bg-transparent outline-none", compact ? "min-w-[60%]" : "min-w-0", "rounded px-1 focus:bg-muted/40", item.task.done && "line-through text-muted-foreground")}
                  />
                ) : (
                  <span className={cn("flex-1 min-w-0 truncate", item.task.done && "line-through text-muted-foreground")}>{item.text}</span>
                )}
                {editable ? (
                  <select
                    value={item.owner_email}
                    onChange={(e) => patch(item, { assignees: e.target.value ? [e.target.value] : [] })}
                    aria-label={t("tasks.meeting.owner", { defaultValue: "Owner" })}
                    className="h-7 max-w-32 px-1 rounded-md border border-border bg-background text-xs"
                  >
                    <option value="">{t("tasks.listView.unassigned", { defaultValue: "Unassigned" })}</option>
                    {members.map((m) => (
                      <option key={m.email} value={m.email}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="text-xs text-muted-foreground truncate max-w-32">{item.owner_name || t("tasks.listView.unassigned", { defaultValue: "Unassigned" })}</span>
                )}
                <DateField
                  label={t("tasks.fields.dueDate", { defaultValue: "Due date" })}
                  value={{ date: item.due_date, time: null }}
                  timeZone={myTimeZone()}
                  disabled={!editable}
                  placeholder="—"
                  onChange={(v) => patch(item, { due_date: v.date, due_time: v.time, time_zone: myTimeZone() })}
                />
                <span className="text-[11px] text-muted-foreground shrink-0 hidden sm:inline">{item.task.status_name}</span>
                {editable && (
                  <button type="button" onClick={() => setRemoving(item)} aria-label={t("tasks.meeting.remove", { defaultValue: "Remove action item" })} className="p-1 rounded text-muted-foreground opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:bg-muted">
                    <X className="size-3.5" />
                  </button>
                )}
              </li>
            ))}
          </ul>
          {editable ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                add();
              }}
              className="flex items-center gap-2 mt-3 flex-wrap"
            >
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={t("tasks.meeting.addPlaceholder", { defaultValue: "Add an action item…" })}
                aria-label={t("tasks.meeting.addPlaceholder", { defaultValue: "Add an action item…" })}
                className="flex-1 min-w-40 h-9 px-3 rounded-xl border border-border bg-background text-sm outline-none focus:border-primary"
              />
              <select value={owner} onChange={(e) => setOwner(e.target.value)} aria-label={t("tasks.meeting.owner", { defaultValue: "Owner" })} className="h-9 px-2.5 rounded-xl border border-border bg-background text-xs">
                <option value="">{t("tasks.meeting.owner", { defaultValue: "Owner" })}</option>
                {members.map((m) => (
                  <option key={m.email} value={m.email}>
                    {m.name}
                  </option>
                ))}
              </select>
              <span className="h-8 px-1 rounded-lg border border-border flex items-center">
                <DateField label={t("tasks.fields.dueDate", { defaultValue: "Due date" })} value={due} timeZone={myTimeZone()} onChange={setDue} placeholder={t("tasks.fields.dueDate", { defaultValue: "Due date" })} />
              </span>
              <button type="submit" disabled={!text.trim()} className="h-9 px-3.5 rounded-xl bg-primary text-primary-foreground text-xs font-bold disabled:opacity-40 flex items-center gap-1">
                <Plus className="size-3.5" /> {t("tasks.meeting.add", { defaultValue: "Add" })}
              </button>
            </form>
          ) : (
            <p className="text-[11px] text-muted-foreground mt-2">{t("tasks.meeting.readOnly", { defaultValue: "Only members of this squad can change these tasks." })}</p>
          )}
        </>
      )}
      {removing && (
        <Modal onClose={() => setRemoving(null)} labelledBy="remove-item-title" className="max-w-sm" z="z-[90]">
          <div className="p-6">
            <h2 id="remove-item-title" className="text-lg font-bold mb-2">
              {t("tasks.meeting.removeTitle", { defaultValue: "Remove this action item?" })}
            </h2>
            <p className="text-sm text-muted-foreground mb-5">{t("tasks.meeting.removeBody", { name: removing.text, defaultValue: "“{{name}}” is also a task. Delete the task too, or keep it?" })}</p>
            <div className="flex flex-col gap-2">
              {(["keep", "delete"] as const).map((choice) => (
                <button
                  key={choice}
                  type="button"
                  onClick={() => {
                    const item = removing;
                    setRemoving(null);
                    captureApi.removeMeetingItem(item.id, choice).then(() => {
                      refetch();
                      toast.success(choice === "delete" ? t("tasks.meeting.removedDeleted", { defaultValue: "Action item and task removed" }) : t("tasks.meeting.removedKept", { defaultValue: "Action item removed; the task stays" }));
                    }, fail);
                  }}
                  className={cn("h-10 rounded-xl text-sm font-bold", choice === "delete" ? "bg-red-500 text-white hover:bg-red-600" : "border border-border hover:bg-muted")}
                >
                  {choice === "delete" ? t("tasks.meeting.deleteTask", { defaultValue: "Delete the task too" }) : t("tasks.meeting.keepTask", { defaultValue: "Keep the task" })}
                </button>
              ))}
              <button type="button" onClick={() => setRemoving(null)} className="h-10 rounded-xl text-sm font-semibold text-muted-foreground hover:bg-muted">
                {t("tasks.cancel", { defaultValue: "Cancel" })}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </section>
  );
}
